import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  OnInit,
  PLATFORM_ID,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Observable, forkJoin, of, skip } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CategoryService } from '../../services/category.service';
import { BannerService } from '../../services/banner.service';
import { StorageService } from '../../services/storage.service';
import { ProductsService } from '../../services/products.service';
import { AnalyticsService } from '../../services/analytics.service';
import { ShopService } from '../../services/shop.service';
import { AuthService } from '../../services/auth.service';
import { OrderService } from '../../services/order.service';
import { ReorderService } from '../../services/reorder.service';
import { RecentlyViewedService } from '../../services/recently-viewed.service';
import { LocationService } from '../../services/location.service';
import { LocationPickerService } from '../../services/location-picker.service';
import { ShopSectionComponent } from '../../shared/components/shop-section/shop-section.component';
import { ShopQuickViewComponent } from '../../shared/components/shop-quick-view/shop-quick-view.component';
import { ProductRailComponent } from '../../shared/components/product-rail/product-rail.component';
import { LoadingSkeletonComponent } from '../../shared/components/loading-skeleton/loading-skeleton.component';
import { Category } from '../../models/category.model';
import { Order } from '../../models/order.model';
import { Shop, ShopType } from '../../models/vendor.model';
import { getOrderPublicKey } from '../../shared/utility/utils.common';
import {
  isActiveOrder, orderEta, orderItemCount, orderRider, orderShopName, orderStatusMeta, paymentMethodLabel,
} from '../../shared/utility/order-status';
import { SUPPORT } from '../../shared/constants/support';
import { categoryLink, isPrintCategory } from '../../shared/utility/print-category';
import { OrderTimelineComponent } from '../../shared/components/order-timeline/order-timeline.component';

interface CategoryShelf {
  category: Category;
  products: any[];
  loading: boolean;
  /** Total products in the category near the user (API count), once loaded. */
  total: number;
}

/** A shelf with this few products looks empty; those categories are reached via the category grid. */
const MIN_PRODUCTS_TO_SHOW_SHELF = 5;

interface HomeBanner {
  id: number | string;
  image: string;
  title: string;
  /** Internal route (routerLink) or absolute URL, when the API provides one. */
  link: string | null;
  external: boolean;
}

/** Shelves rendered up-front; further shelves load as the user scrolls near them. */
const INITIAL_SHELVES = 3;
const SHELF_BATCH = 2;
const MAX_SHELVES = 10;
const SHELF_PAGE_SIZE = 12;
const RECENT_ORDER_COUNT = 3;

/**
 * Homepage. Everything shown here comes from an API (banners, categories,
 * shops, products, orders) or the user's own device (recently viewed);
 * sections without real data are simply not rendered.
 */
@Component({
  standalone: true,
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  imports: [CommonModule, RouterLink, ShopSectionComponent, ProductRailComponent, LoadingSkeletonComponent, OrderTimelineComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HomeComponent implements OnInit, AfterViewInit {
  private router = inject(Router);
  private categoryService = inject(CategoryService);
  private bannerService = inject(BannerService);
  private storage = inject(StorageService);
  private productService = inject(ProductsService);
  private analytics = inject(AnalyticsService);
  private modal = inject(NgbModal);
  private shopService = inject(ShopService);
  private authService = inject(AuthService);
  private orderService = inject(OrderService);
  private reorderService = inject(ReorderService);
  private toastr = inject(ToastrService);
  private destroyRef = inject(DestroyRef);
  private recentlyViewedService = inject(RecentlyViewedService);
  private locationService = inject(LocationService);
  private locationPicker = inject(LocationPickerService);
  private zone = inject(NgZone);
  readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  // ── Banners ──
  readonly banners = signal<HomeBanner[]>([]);
  readonly bannersLoading = signal(true);
  readonly activeBanner = signal(0);
  private bannerTrack = viewChild<ElementRef<HTMLElement>>('bannerTrack');
  private bannerTimer?: ReturnType<typeof setInterval>;
  private bannerPaused = false;

  // ── Categories & product shelves ──
  readonly categories = signal<Category[]>([]);
  readonly hasPrintCategory = computed(() => this.categories().some(isPrintCategory));
  readonly categoryLink = categoryLink;
  readonly categoriesLoading = signal(true);
  readonly shelves = signal<CategoryShelf[]>([]);
  private shelfSentinel = viewChild<ElementRef<HTMLElement>>('shelfSentinel');
  private shelfObserver?: IntersectionObserver;
  private shelfSource: Category[] = [];

  // ── Shops ──
  readonly stores = signal<Shop[]>([]);
  readonly loadingStores = signal(true);
  readonly featuredShops = computed(() => this.stores().filter(s => s.is_featured));
  readonly serviceProviderShops = computed(() =>
    this.stores().filter(s => s.is_service_provider || s.shop_type === ShopType.SERVICE || s.shop_type === ShopType.HYBRID));
  readonly productShops = computed(() =>
    this.stores().filter(s => !(s.shop_type === ShopType.SERVICE)));

  // ── Personal ──
  readonly recentlyViewed = this.recentlyViewedService.items;
  readonly recentOrders = signal<Order[]>([]);
  readonly recentOrdersLoading = signal(false);
  readonly reorderingId = signal<number | null>(null);
  readonly isLoggedIn = toSignal(this.authService.isLoggedIn$, { initialValue: false });
  readonly locationPromptDismissed = signal(false);
  readonly currentLocation = toSignal(this.locationService.currentLocation$, { initialValue: null });

  ngOnInit(): void {
    this.loadBanners();
    this.loadCategories();
    // Shops and product shelves depend on the delivery location stored in the browser,
    // so the server renders their skeletons and the browser fills them in.
    if (this.isBrowser) {
      this.fetchStores();
    }

    // Address / radius changes refresh the shop list (skip the BehaviorSubject replay).
    this.shopService.refreshStores$.pipe(skip(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.fetchStores();
      this.resetShelves();
    });

    if (this.isBrowser) {
      this.authService.isLoggedIn$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(loggedIn => {
        if (loggedIn) this.loadRecentOrders();
        else this.recentOrders.set([]);
      });
    }

    this.destroyRef.onDestroy(() => {
      clearInterval(this.bannerTimer);
      this.shelfObserver?.disconnect();
    });
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser || typeof IntersectionObserver === 'undefined') return;

    // Load more category shelves when the user nears the end of the page.
    this.shelfObserver = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) {
        this.appendShelves(SHELF_BATCH);
      }
    }, { rootMargin: '600px 0px' });

    const sentinel = this.shelfSentinel()?.nativeElement;
    if (sentinel) this.shelfObserver.observe(sentinel);
  }

  // ────────────────────────── BANNERS ──────────────────────────

  private loadBanners(): void {
    this.bannerService.getBanners().pipe(
      map((res: any) => (res?.results || res || []) as any[]),
      catchError(() => of([] as any[])),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(list => {
      this.banners.set(list.filter(b => b?.image).map(b => this.toBanner(b)));
      this.bannersLoading.set(false);
      this.startBannerAutoplay();
    });
  }

  private toBanner(b: any): HomeBanner {
    const rawLink: string | null = b.link || b.url || b.redirect_url || b.target_url || null;
    const external = !!rawLink && /^https?:\/\//i.test(rawLink) && !rawLink.includes('thenearbyshop.com');
    let link = rawLink;
    if (rawLink && !external) {
      // Same-site absolute URLs become router paths, so tapping a banner never reloads the app.
      link = rawLink.replace(/^https?:\/\/(www\.)?thenearbyshop\.com/i, '') || '/';
    }
    return { id: b.id ?? b.image, image: b.image, title: b.title || b.name || 'Offer', link, external };
  }

  private startBannerAutoplay(): void {
    if (!this.isBrowser || this.banners().length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    clearInterval(this.bannerTimer);
    // Outside the zone: a recurring timer inside it keeps the app from ever becoming
    // "stable", which blocks hydration cleanup (server-rendered nodes stay duplicated).
    // Signal writes still schedule change detection.
    this.zone.runOutsideAngular(() => {
      this.bannerTimer = setInterval(() => {
        if (this.bannerPaused || document.hidden) return;
        this.goToBanner((this.activeBanner() + 1) % this.banners().length);
      }, 5000);
    });
  }

  pauseBanners(paused: boolean): void {
    this.bannerPaused = paused;
  }

  goToBanner(index: number): void {
    const track = this.bannerTrack()?.nativeElement;
    if (!track) return;
    track.scrollTo({ left: track.clientWidth * index, behavior: 'smooth' });
    this.activeBanner.set(index);
  }

  onBannerScroll(track: HTMLElement): void {
    const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
    if (index !== this.activeBanner()) this.activeBanner.set(index);
  }

  onBannerClick(banner: HomeBanner): void {
    this.analytics.trackEvent('BANNER_CLICKED', 'PROMOTION', 1, String(banner.id));
  }

  // ───────────────────── CATEGORIES & SHELVES ─────────────────────

  private loadCategories(): void {
    this.categoryService.getCategories().pipe(
      catchError(() => of({ results: [] })),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((res: any) => {
      const all = (res?.results || []) as Category[];
      this.categories.set(all);
      this.categoriesLoading.set(false);

      // Featured categories get shelves first; fall back to all categories if none are flagged.
      const featured = all.filter(c => c.is_featured);
      this.shelfSource = (featured.length ? featured : all).slice(0, MAX_SHELVES);
      this.resetShelves();
    });
  }

  private resetShelves(): void {
    if (!this.isBrowser) {
      // Same placeholder the browser starts with, so hydration doesn't swap layouts.
      this.shelves.set(this.shelfSource.slice(0, INITIAL_SHELVES).map(category => ({ category, products: [], loading: true, total: 0 })));
      return;
    }
    this.shelves.set([]);
    this.appendShelves(INITIAL_SHELVES);
  }

  private appendShelves(count: number): void {
    const existing = this.shelves();
    if (existing.some(s => s.loading)) return; // one batch at a time

    const next = this.shelfSource.slice(existing.length, existing.length + count);
    if (!next.length) return;

    const added: CategoryShelf[] = next.map(category => ({ category, products: [], loading: true, total: 0 }));
    this.shelves.set([...existing, ...added]);
    added.forEach(shelf => this.loadShelf(shelf));
  }

  private loadShelf(shelf: CategoryShelf): void {
    const pincode = this.storage.getItem('postal_code');
    const request$: Observable<any> = pincode
      ? this.productService.getProductsByCategorySlugPaginationPincode(shelf.category.slug, pincode, 1, SHELF_PAGE_SIZE)
      : this.productService.getProductsByCategorySlugPagination(shelf.category.slug, 1, SHELF_PAGE_SIZE);

    request$.pipe(
      map(res => ({ products: (res?.results || []) as any[], total: Number(res?.count) || (res?.results || []).length })),
      catchError(() => of({ products: [] as any[], total: 0 })),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ products, total }) => {
      // Shelves are replaced immutably so OnPush children see new inputs.
      this.shelves.update(list => list.map(s =>
        s.category.id === shelf.category.id ? { ...s, products, total, loading: false } : s));
      this.loadMoreIfSentinelVisible();
    });
  }

  /** The observer only fires on visibility changes; if empty shelves keep the page
   *  short, the sentinel never leaves the viewport, so check again after each batch. */
  private loadMoreIfSentinelVisible(): void {
    if (!this.isBrowser || this.shelves().some(s => s.loading)) return;
    const sentinel = this.shelfSentinel()?.nativeElement;
    if (!sentinel) return;
    requestAnimationFrame(() => {
      if (sentinel.getBoundingClientRect().top < window.innerHeight + 600) {
        this.appendShelves(SHELF_BATCH);
      }
    });
  }

  showShelf(shelf: CategoryShelf): boolean {
    return shelf.loading || shelf.total > MIN_PRODUCTS_TO_SHOW_SHELF;
  }

  trackCategory(cat: Category): void {
    this.analytics.trackEvent('CATEGORY_PAGE_REDIRECT_CLICKED', 'PAGE_VISIT', 1, `CATEGORY: ${cat.slug}`);
  }

  // ─────────────────────────── SHOPS ───────────────────────────

  fetchStores(): void {
    this.loadingStores.set(true);

    const postalCode = this.storage.getItem('postal_code');
    const lat = this.storage.getItem('latitude');
    const lng = this.storage.getItem('longitude');
    const radius = this.storage.getItem('radius') || '1';

    // Priority: precise coordinates, then pincode, then the general list.
    const request$: Observable<any> = lat && lng
      ? this.categoryService.getStoresByLocation(lat, lng, radius)
      : postalCode
        ? this.categoryService.getStoresByPincode(postalCode)
        : this.categoryService.getStores();

    request$.pipe(
      catchError(() => of({ results: [] })),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((res: any) => {
      this.stores.set(res?.results || []);
      this.loadingStores.set(false);
    });
  }

  openShopQuickView(shop: Shop): void {
    this.analytics.trackEvent('SHOP_QUICK_VIEW_OPENED', 'EVENT_TRIGGERED', 1, `STORE: ${shop.slug}`);
    const modalRef = this.modal.open(ShopQuickViewComponent, { centered: true, size: 'md' });
    modalRef.componentInstance.shop = shop;
  }

  openLocationPicker(): void {
    this.locationPicker.open();
  }

  openWhatsAppNotify(): void {
    if (!this.isBrowser) return;
    const area = this.storage.getItem('postal_code') || '';
    const message = encodeURIComponent(
      `Hi! I'd like TheNearByShop to launch in my area${area ? ' (' + area + ')' : ''}. Please let me know when it's available.`
    );
    this.analytics.trackEvent('AREA_NOTIFY_WHATSAPP_CLICKED', 'LEAD_CAPTURE', 1, `PINCODE: ${area}`);
    window.open(`https://wa.me/${SUPPORT.whatsapp}?text=${message}`, '_blank', 'noopener');
  }

  // ────────────────────── RECENT ORDERS / REORDER ──────────────────────

  /**
   * The order-history list returns summaries (often without items, shop or
   * delivery details), so the few orders shown here are re-read from the order
   * detail endpoint. A detail failure falls back to the summary.
   */
  private loadRecentOrders(): void {
    this.recentOrdersLoading.set(true);
    this.orderService.getOrderHistory().pipe(
      map(res => (res?.results || []).slice(0, RECENT_ORDER_COUNT)),
      switchMap(summaries => summaries.length
        ? forkJoin(summaries.map(o => this.orderService.getOrderSummary(o.id).pipe(catchError(() => of(o)))))
        : of([] as Order[])),
      catchError(() => of([] as Order[])),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(orders => {
      this.recentOrders.set(orders);
      this.recentOrdersLoading.set(false);
    });
  }

  // Template helpers (shared with the profile's orders section)
  readonly statusMeta = orderStatusMeta;
  readonly shopName = orderShopName;
  readonly itemCount = orderItemCount;
  readonly rider = orderRider;
  readonly eta = orderEta;
  readonly paymentLabel = paymentMethodLabel;
  readonly isActive = isActiveOrder;

  orderKey(order: Order): string {
    return getOrderPublicKey(order.id);
  }

  orderItemsLabel(order: Order): string {
    const names = (order.items || []).map(i => i.product_name || i.product).filter(Boolean) as string[];
    if (!names.length) {
      const count = orderItemCount(order);
      return count ? `${count} item${count === 1 ? '' : 's'}` : '';
    }
    return names.length > 2 ? `${names.slice(0, 2).join(', ')} +${names.length - 2} more` : names.join(', ');
  }

  canReorder(order: Order): boolean {
    return this.reorderService.canReorder(order);
  }

  reorder(order: Order): void {
    if (this.reorderingId()) return;
    this.reorderingId.set(order.id);
    this.analytics.trackEvent('REORDER_CLICKED', 'ECOMMERCE', 1, `ORDER: ${order.id}`);

    this.reorderService.reorder(order).subscribe({
      next: ({ added, failed }) => {
        this.reorderingId.set(null);
        if (added && !failed.length) {
          this.toastr.success(`${added} item(s) added to your cart`);
        } else if (added) {
          this.toastr.warning(`${added} added. Unavailable now: ${failed.join(', ')}`);
        } else {
          this.toastr.error('These items are not available right now.');
          return;
        }
        this.router.navigate(['/cart']);
      },
      error: () => {
        this.reorderingId.set(null);
        this.toastr.error('Could not add these items. Please try again.');
      }
    });
  }

  statusTone(status: string): string {
    const tone = orderStatusMeta(status).tone;
    return tone === 'success' ? 'success' : tone === 'issue' ? 'danger' : 'info';
  }
}
